import {
  BackstageTypography,
  createUnifiedTheme,
  genPageTheme,
  palettes,
  pageTheme as defaultPageThemes,
  shapes,
  UnifiedTheme,
} from '@backstage/theme';
import { jetbrainsMono, openSans, spaceGrotesk } from './fonts';
import {
  ByxColorScheme,
  byxBrand,
  byxDark,
  byxLight,
  byxSidebar,
  byxStatus,
  byxViolet,
} from './tokens';

// Escala do protótipo: h1 42px (páginas), h2 36px (títulos de formulário),
// h3 24px (cards de destaque), h5/h6 500 para títulos de card.
const byxTypography: BackstageTypography = {
  htmlFontSize: 16,
  fontFamily: openSans,
  h1: { fontSize: 42, fontWeight: 700, marginBottom: 10 },
  h2: { fontSize: 36, fontWeight: 700, marginBottom: 8 },
  h3: { fontSize: 24, fontWeight: 700, marginBottom: 6 },
  h4: { fontSize: 20, fontWeight: 700, marginBottom: 6 },
  h5: { fontSize: 18, fontWeight: 500, marginBottom: 4 },
  h6: { fontSize: 16, fontWeight: 500, marginBottom: 2 },
};

/** MUI's placeholder selectors, in `color` at full opacity. */
function placeholderStyles(color: string) {
  const placeholder = { color, opacity: 1 };
  return {
    '&::-webkit-input-placeholder': placeholder,
    '&::-moz-placeholder': placeholder,
    '&:-ms-input-placeholder': placeholder,
    '&::-ms-input-placeholder': placeholder,
  };
}

const displayFont = (letterSpacing: string) => ({
  fontFamily: spaceGrotesk,
  letterSpacing,
});

// Space Grotesk no chrome da UI (títulos, botões, tabs, cabeçalhos de
// tabela); Open Sans permanece no texto corrido via typography.fontFamily.
/**
 * Keyboard focus on stock controls (U8-6, design system "Sprint 9 planning
 * rulings"): the 2px primary outline with a 2px offset, as the Input field
 * and the Sprint 7 controls.
 */
export const focusRing = (scheme: ByxColorScheme) => ({
  outline: `2px solid ${scheme.accent}`,
  outlineOffset: 2,
});

/** The same ring drawn inside the control, where an ancestor clips. */
export const focusInset = (scheme: ByxColorScheme) => ({
  ...focusRing(scheme),
  outlineOffset: -2,
});

// Função porque hover e linhas divisórias variam por scheme. Atenção:
// createUnifiedTheme faz merge RASO por componente — sobrescrever um
// Mui* descarta o default do Backstage inteiro para aquele componente,
// então os defaults necessários são replicados aqui.
const byxComponents = (scheme: ByxColorScheme, status: ByxStatusPalette) => ({
  // Every ButtonBase control (buttons, icon buttons, tabs, list and menu
  // items, clickable chips, checkboxes, switches) shows the ring on
  // `.Mui-focusVisible`, MUI's keyboard-focus class, instead of the stock
  // tint or ripple alone. Written with two parts so the unified theme does
  // not turn it into a global `.Mui-focusVisible` rule; an app control with
  // its own doubled-class ring (executions `focusInset`) still wins.
  MuiButtonBase: {
    styleOverrides: {
      root: {
        '&.Mui-focusVisible, &:focus-visible': focusRing(scheme),
      },
    },
  },
  // Rows fill their list, and menus and filter boxes clip them: inset.
  MuiListItem: {
    styleOverrides: {
      root: {
        '&.MuiListItem-button.Mui-focusVisible': focusInset(scheme),
      },
    },
  },
  // The switch root hides its overflow: the ring inside the thumb's 38px
  // hit area, around the thumb.
  MuiSwitch: {
    styleOverrides: {
      switchBase: {
        '&.MuiSwitch-switchBase.Mui-focusVisible': focusInset(scheme),
      },
    },
  },
  MuiTypography: {
    styleOverrides: {
      h1: displayFont('-0.03em'),
      h2: displayFont('-0.03em'),
      h3: displayFont('-0.02em'),
      h4: displayFont('-0.02em'),
      h5: displayFont('-0.01em'),
      h6: displayFont('-0.01em'),
      overline: {
        fontFamily: spaceGrotesk,
        fontWeight: 500,
        letterSpacing: '0.16em',
      },
    },
  },
  // Abas do design system (Tabs): rótulo 14px 500 em muted-text; a ativa
  // em ink 600, com sublinhado de 2px no accent em toda a largura.
  MuiTab: {
    styleOverrides: {
      root: {
        fontFamily: spaceGrotesk,
        fontSize: 14,
        fontWeight: 500,
        textTransform: 'none' as const,
        '&.Mui-selected': { color: scheme.ink, fontWeight: 600 },
        // The Tabs root and scroller hide their overflow (U7-10): the ring
        // inside the tab, radius-sm, as the Sprint 7 tabs.
        '&.MuiTab-root.Mui-focusVisible': {
          ...focusInset(scheme),
          borderRadius: 6,
        },
      },
      textColorInherit: {
        color: scheme.mutedText,
        opacity: 1,
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      indicator: { height: 2, backgroundColor: scheme.accent },
    },
  },
  // Painéis planos do v2: borda hairline, raio de 14px e sem sombra. Só a
  // elevação padrão (Card, InfoCard, Paper comum); menus, popovers e
  // diálogos mantêm a sombra deles.
  MuiPaper: {
    styleOverrides: {
      elevation1: {
        boxShadow: 'none',
        border: `1px solid ${scheme.line}`,
        borderRadius: 14,
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        boxShadow: 'none',
        border: `1px solid ${scheme.line}`,
        borderRadius: 14,
      },
    },
  },
  // Tags como chips mono (Tag do design system): JetBrains Mono 12px sobre
  // chip/chipInk, raio de 6px.
  MuiChip: {
    styleOverrides: {
      root: {
        fontFamily: jetbrainsMono,
        fontSize: 12,
        borderRadius: 6,
        backgroundColor: scheme.chip,
        color: scheme.chipInk,
        // A deletable chip is focusable without being a ButtonBase.
        '&:focus-visible': focusRing(scheme),
      },
    },
  },
  // Tabelas do design: linhas planas separadas por hairline (sem zebra)
  // e highlight no hover. Densidade replicada do defaultComponentThemes.
  MuiTableCell: {
    styleOverrides: {
      root: {
        wordBreak: 'break-word' as const,
        overflow: 'hidden',
        verticalAlign: 'middle',
        lineHeight: '1',
        margin: 0,
        padding: '24px 16px 24px 20px',
        borderBottom: `1px solid ${scheme.line}`,
      },
      sizeSmall: {
        padding: '12px 16px 12px 20px',
      },
      // Cabeçalhos em sentence case, 13px 600, muted-text, sobre surface-2
      // (Table do design system). O Table do core-components pinta inline a
      // cor da coluna "highlight" (nome); o !important mantém a regra única.
      head: {
        wordBreak: 'break-word' as const,
        overflow: 'hidden',
        color: `${scheme.mutedText} !important`,
        backgroundColor: scheme.surface2,
        lineHeight: '1',
        fontFamily: spaceGrotesk,
        fontSize: 13,
        fontWeight: 600,
        textTransform: 'none' as const,
      },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        '&:nth-of-type(odd)': { backgroundColor: 'transparent' },
        'tbody > &:hover': { backgroundColor: scheme.hover },
      },
      hover: { '&:hover': { cursor: 'pointer' } },
      head: {
        '&:nth-of-type(odd)': { backgroundColor: 'transparent' },
      },
    },
  },
  BackstageTableHeader: {
    styleOverrides: {
      header: {
        borderTop: 'none',
        borderBottom: `1px solid ${scheme.line}`,
        color: scheme.mutedText,
        fontSize: 13,
        fontWeight: 600,
        textTransform: 'none' as const,
      },
    },
  },
  // Rótulo de ordenação: herda o muted-text do cabeçalho, também na coluna
  // ordenada (o padrão do MUI a pinta em ink).
  MuiTableSortLabel: {
    styleOverrides: {
      root: {
        color: 'inherit',
        '&:hover, &:focus': { color: 'inherit' },
      },
      active: { color: 'inherit !important' },
    },
  },
  // Botões do design v2: cantos arredondados (10px, como os filtros e os
  // painéis) e planos, sem sombra.
  MuiButton: {
    styleOverrides: {
      root: {
        fontFamily: spaceGrotesk,
        fontWeight: 500,
        textTransform: 'none' as const,
        borderRadius: 10,
      },
      contained: {
        boxShadow: 'none',
        '&:hover': { boxShadow: 'none' },
        '&:active': { boxShadow: 'none' },
      },
      // D-24: a filled primary button paints primary-fill with on-primary,
      // never `primary.main` (the dark accent is a light lilac, made for
      // text). The disabled rule of `contained` still wins (two classes).
      containedPrimary: {
        backgroundColor: scheme.accentFill,
        color: scheme.accentInk,
        '&:hover': {
          backgroundColor: scheme.accentFillHover,
          '@media (hover: none)': { backgroundColor: scheme.accentFill },
        },
      },
    },
  },
  // Stock pickers (catalog filters, rjsf) render checkboxes with MUI's
  // default `secondary` colour, the brand navy, invisible on the dark panel
  // (X9). Primary is readable on both panels.
  MuiCheckbox: {
    defaultProps: { color: 'primary' as const },
  },
  // Placeholders in muted-text at full opacity (X6): MUI's default is the
  // text colour at 42 %, 2.8:1 on the light panel. Same keys as MUI's, so
  // the hide-until-focus rule for an unshrunk label keeps working.
  MuiInputBase: {
    styleOverrides: {
      input: placeholderStyles(scheme.mutedText),
    },
  },
  // Inputs do formulário no design: caixa com borda hairline sobre o bg
  // da página, foco no accent — o rjsf do scaffolder usa TextField
  // standard (underline), que aqui vira caixa.
  MuiInput: {
    styleOverrides: {
      root: {
        border: `1px solid ${scheme.line}`,
        borderRadius: 4,
        backgroundColor: scheme.bg,
        paddingLeft: 12,
        paddingRight: 12,
        transition: 'border-color 0.15s ease',
        '&.Mui-focused': { borderColor: scheme.accent },
        // The same red as the field's label and message (CR8F-8): claro
        // #c8362b, escuro #ff8a80.
        '&.Mui-error': { borderColor: status.error },
      },
      underline: {
        '&:before, &:after': { display: 'none' },
      },
      input: { padding: '11px 0' },
    },
  },
  // Label sempre na posição "shrink", acima da caixa. Na posição de
  // placeholder ele ficava atrás do fundo do input (pintado depois dele) e
  // o campo vazio aparecia sem nome até receber foco.
  MuiInputLabel: {
    styleOverrides: {
      formControl: { transform: 'translate(0, 1.5px) scale(0.75)' },
      shrink: { transform: 'translate(0, 1.5px) scale(0.75)' },
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: { fontFamily: spaceGrotesk, fontWeight: 500 },
      asterisk: { color: scheme.accent },
    },
  },
  // Stepper do wizard do scaffolder ("01. Detalhes / 02. Revisar").
  MuiStepper: {
    styleOverrides: {
      root: { backgroundColor: 'transparent' },
    },
  },
  // Step labels in every stepper (U8F-2): upcoming body-large 500 in
  // muted-text, active and completed 600 in ink, as on Registrar existente.
  MuiStepLabel: {
    styleOverrides: {
      label: {
        fontFamily: openSans,
        fontSize: 14,
        fontWeight: 500,
        lineHeight: '20px',
        color: scheme.mutedText,
        '&.MuiStepLabel-active, &.MuiStepLabel-completed': {
          fontWeight: 600,
          color: scheme.ink,
        },
      },
    },
  },
  // The design system stepper rule for every stock StepIcon (the scaffolder
  // wizard), as on the Registrar existente page (R8-2): upcoming circles in
  // surface-2 with a 1px border-strong ring and a muted-text number; active
  // and completed circles in primary-fill, the active number in on-primary.
  // Stock paints upcoming in text.disabled under a white number (2.87:1) and
  // the active one in `primary.main`, the dark text lilac. Same two-class
  // selectors as the stock rules, written after them, so these win.
  MuiStepIcon: {
    styleOverrides: {
      root: {
        color: scheme.surface2,
        '& circle': { stroke: scheme.borderStrong, strokeWidth: 1 },
        '&.MuiStepIcon-active, &.MuiStepIcon-completed': {
          color: scheme.accentFill,
          '& circle': { stroke: 'none' },
        },
        '&.MuiStepIcon-active .MuiStepIcon-text': { fill: scheme.accentInk },
        // The completed CheckCircle draws its check as a cut-out, so the
        // page showed through it (CR8F-1). An on-primary disc behind the
        // icon, smaller than the circle so its edge never shows, makes the
        // check white in both themes.
        '&.MuiStepIcon-completed': {
          backgroundImage: `radial-gradient(circle closest-side, ${scheme.accentInk} 84%, transparent 86%)`,
        },
      },
      text: {
        fill: scheme.mutedText,
        fontFamily: spaceGrotesk,
        fontWeight: 700,
      },
    },
  },
  // Barra de progresso com o gradiente do protótipo (accent → dark
  // accent → âmbar).
  MuiLinearProgress: {
    styleOverrides: {
      bar: {
        // The middle stop is pinned: it read `byxDark.accent`, which D-24
        // moved to a text lilac.
        backgroundImage: `linear-gradient(90deg, ${scheme.accent}, ${byxViolet} 70%, ${byxBrand.amber})`,
      },
    },
  },
  // Wrapper do formulário do scaffolder: o card do design (o InfoCard
  // externo fica transparente via wizard.css) e botões centralizados.
  // Largo o bastante para listas aninhadas (rota → métodos → scopes) não
  // espremerem os campos.
  BackstageTemplateStepper: {
    styleOverrides: {
      formWrapper: {
        padding: 32,
        backgroundColor: scheme.panel,
        border: `1px solid ${scheme.line}`,
        borderRadius: 14,
        maxWidth: 960,
        margin: '0 auto',
      },
      footer: {
        justifyContent: 'center',
        gap: 12,
        marginTop: 24,
      },
    },
  },
  // Conector do stepper na cor de linha do tema.
  MuiStepConnector: {
    styleOverrides: {
      line: { borderColor: scheme.line },
    },
  },
  // Catalog filters from the v2 design: rounded (10px) boxes on hairlines
  // and small muted labels. By name, so every page reusing these pickers
  // (catalog, APIs, docs…) gets the same look.
  CatalogReactUserListPicker: {
    styleOverrides: {
      root: {
        backgroundColor: 'transparent',
        boxShadow: 'none',
        // É um Card: sem a borda dos painéis, que fica nos grupos.
        border: 'none',
        borderRadius: 0,
        // Counts beside each item, small and muted: text, so muted-text (X3).
        '& .MuiListItemSecondaryAction-root': {
          fontSize: 12,
          color: scheme.mutedText,
        },
      },
      // Títulos dos grupos de filtro: label 12px 600 em muted-text.
      title: {
        display: 'block',
        margin: '0 0 6px',
        textTransform: 'none' as const,
        fontSize: 12,
        fontWeight: 600,
        color: scheme.mutedText,
      },
      // Caixas de filtro com border-strong (Input field do design system).
      groupWrapper: {
        margin: '0 0 16px',
        borderRadius: 10,
        border: `1px solid ${scheme.borderStrong}`,
        boxShadow: 'none',
        backgroundColor: scheme.panel,
        overflow: 'hidden',
        '&:last-child': { marginBottom: 0 },
      },
      listIcon: {
        minWidth: 26,
        '& svg': { fontSize: 16 },
      },
      menuItem: {
        minHeight: 44,
        '& .MuiTypography-body1': { fontSize: 14 },
        '&.Mui-selected, &.Mui-selected:hover': {
          backgroundColor: scheme.hover,
          fontWeight: 600,
        },
      },
    },
  },
  // Each picker already sits in a box with 8px above and below; without the
  // control's own 8px margins the filters are ~16px apart, as in the design.
  BackstageAutocomplete: {
    styleOverrides: {
      root: { margin: 0 },
      label: { fontSize: 12, fontWeight: 600, color: scheme.mutedText },
    },
  },
  // 14px text in 40px boxes, the design's filter density.
  BackstageAutocompleteBase: {
    styleOverrides: {
      // Same nested selectors as the component's own size rules, so these win.
      inputRoot: {
        borderRadius: 10,
        backgroundColor: scheme.panel,
        '& fieldset': { borderColor: scheme.borderStrong },
        '$root$hasClearIcon$hasPopupIcon &': { paddingBlock: 5 },
      },
      input: {
        '$root$hasClearIcon$hasPopupIcon &': { fontSize: 14 },
      },
    },
  },
  BackstageSelect: {
    styleOverrides: {
      formControl: { margin: 0 },
      label: {
        fontSize: 12,
        fontWeight: 600,
        color: scheme.mutedText,
        '&.Mui-focused': { color: scheme.mutedText },
      },
      formLabel: {
        fontSize: 12,
        fontWeight: 600,
        color: scheme.mutedText,
        '&.Mui-focused': { color: scheme.mutedText },
      },
    },
  },
  // Busca das listas com filtros (ex.: templates na página Criar): caixa
  // de 40px arredondada como os demais filtros.
  CatalogReactEntitySearchBar: {
    styleOverrides: {
      searchToolbar: { minHeight: 0 },
      input: {
        borderRadius: 10,
        backgroundColor: scheme.panel,
        fontSize: 14,
        '& svg': { fontSize: 20 },
      },
    },
  },
  // Filtro de categorias da página Criar, no mesmo traço dos demais.
  ScaffolderReactTemplateCategoryPicker: {
    styleOverrides: {
      label: {
        display: 'block',
        marginBottom: 6,
        textTransform: 'none' as const,
        fontSize: 12,
        fontWeight: 600,
        color: scheme.mutedText,
      },
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: 10,
          backgroundColor: scheme.panel,
          fontSize: 14,
        },
        '& fieldset': { borderColor: scheme.borderStrong },
      },
    },
  },
  BackstageSelectInputBase: {
    styleOverrides: {
      input: {
        borderRadius: 10,
        border: `1px solid ${scheme.borderStrong}`,
        backgroundColor: scheme.panel,
        fontSize: 14,
        lineHeight: '20px',
        padding: '9px 26px 9px 12px',
        '&:focus': { borderRadius: 10, backgroundColor: scheme.panel },
        // The selected value is a body1 paragraph (16px on a 24px line).
        '& .MuiTypography-root': { fontSize: 'inherit', lineHeight: 'inherit' },
      },
    },
  },
});

// Cabeçalho de página sólido navy (hero do protótipo) em todas as páginas.
const byxPageTheme = Object.fromEntries(
  Object.keys(defaultPageThemes).map(key => [
    key,
    genPageTheme({
      colors: [byxBrand.navy, byxBrand.indigo],
      shape: shapes.wave,
    }),
  ]),
);

/**
 * The MUI status palette (`palette.X.main`). Stock components read these as
 * text (rjsf / MUI error labels and messages, the AsyncAPI viewer's
 * `text-orange-600` and `text-blue-500`), so each `main` passes AA as text on
 * its theme's panel and surface-2 (design system "MUI status palette as
 * text").
 */
export interface ByxStatusPalette {
  error: string;
  /**
   * `error.light`, where MUI's derived one (main lightened by 20 %) is not
   * readable as text: the stock Templating Extensions page paints its
   * required-argument asterisks in it (X17).
   */
  errorLight?: string;
  warning: string;
  success: string;
  info: string;
}

/**
 * Claro: error stays #c8362b (stock error text only, 5.23:1; app badges use
 * `error`). Warning, success and info are the scheme inks: #d6961e and
 * #3e8ed0 were 2.55 and 3.51:1 as AsyncAPI text, and the #3fb27f green
 * (2.66:1) is never text, yet stock derives text and marks from it (the
 * TechDocs checked task, OpenAPI property types).
 */
export const byxLightStatus: ByxStatusPalette = {
  error: byxStatus.error,
  // X17: the derived #d35e55 is 3.79:1 on the panel; the stock error ink
  // itself is 5.23:1.
  errorLight: byxStatus.error,
  warning: byxLight.warningInk,
  success: byxLight.successInk,
  info: byxLight.info,
};

/** Escuro: error is the scheme ink #ff8a80 (#c8362b was 3.56:1, U8-1). */
export const byxDarkStatus: ByxStatusPalette = {
  error: byxDark.error,
  warning: byxStatus.warning,
  success: byxStatus.success,
  info: byxStatus.info,
};

function createByxTheme(
  base: typeof palettes.light | typeof palettes.dark,
  scheme: ByxColorScheme,
  status: ByxStatusPalette,
): UnifiedTheme {
  return createUnifiedTheme({
    palette: {
      ...base,
      // contrastText stays automatic (D-24): dark text on the dark lilac, so
      // stock fills on `primary.main` (StepIcon, TechDocs) stay readable.
      primary: { main: scheme.accent },
      secondary: { main: byxBrand.navy },
      background: { default: scheme.bg, paper: scheme.panel },
      // Secondary text is muted-text (X3); `mute` is for icons and strokes.
      text: { primary: scheme.ink, secondary: scheme.mutedText },
      divider: scheme.line,
      link: scheme.accent,
      linkHover: scheme.accent,
      // Stock CodeSnippet line numbers (X10).
      textVerySubtle: scheme.mutedText,
      error: {
        main: status.error,
        ...(status.errorLight ? { light: status.errorLight } : {}),
      },
      success: { main: status.success },
      warning: { main: status.warning },
      info: { main: status.info },
      navigation: {
        background: byxSidebar.background,
        indicator: byxBrand.amber,
        color: byxSidebar.inkMuted,
        selectedColor: byxSidebar.ink,
        navItem: { hoverBackground: byxSidebar.hover },
        submenu: { background: byxSidebar.background },
      },
    },
    defaultPageTheme: 'home',
    pageTheme: byxPageTheme,
    fontFamily: openSans,
    typography: byxTypography,
    components: byxComponents(scheme, status),
  });
}

export const byxLightTheme = createByxTheme(
  palettes.light,
  byxLight,
  byxLightStatus,
);

export const byxDarkTheme = createByxTheme(
  palettes.dark,
  byxDark,
  byxDarkStatus,
);
